import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  useTheme,
  Stack,
  Link,
  Chip,
  alpha,
} from '@mui/material';
import {
  Lock,
  Security,
  Shield,
  Storage,
  Analytics,
  Cookie,
  Email,
  Phone,
  Language,
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// Markdown components for proper rendering
const MarkdownComponents = (theme) => ({
  h1: ({ children }) => (
    <Typography 
      variant="h3" 
      component="h1" 
      sx={{ 
        mb: 3, 
        mt: 4,
        fontWeight: 600, 
        color: 'text.primary',
      }}
    >
      {children}
    </Typography>
  ),
  h2: ({ children }) => (
    <Typography 
      variant="h4" 
      component="h2" 
      sx={{ 
        mt: 6, 
        mb: 3, 
        fontWeight: 600, 
        color: 'text.primary',
        borderBottom: '1px solid',
        borderColor: 'divider',
        pb: 1,
      }}
    >
      {children}
    </Typography>
  ),
  h3: ({ children }) => (
    <Typography 
      variant="h5" 
      component="h3" 
      sx={{ 
        mt: 4, 
        mb: 2, 
        fontWeight: 600, 
        color: 'text.primary',
      }}
    >
      {children}
    </Typography>
  ),
  p: ({ children }) => (
    <Typography 
      variant="body1" 
      sx={{ 
        mb: 2, 
        lineHeight: 1.7, 
        color: 'text.primary' 
      }}
    >
      {children}
    </Typography>
  ),
  strong: ({ children }) => (
    <Typography 
      component="strong" 
      sx={{ 
        fontWeight: 600, 
        color: 'text.primary' 
      }}
    >
      {children}
    </Typography>
  ),
  ul: ({ children }) => (
    <Box component="ul" sx={{ mb: 2, pl: 3, color: 'text.primary' }}>
      {children}
    </Box>
  ),
  ol: ({ children }) => (
    <Box component="ol" sx={{ mb: 2, pl: 3, color: 'text.primary' }}>
      {children}
    </Box>
  ),
  li: ({ children }) => (
    <Typography component="li" sx={{ mb: 0.5, lineHeight: 1.6 }}>
      {children}
    </Typography>
  ),
  blockquote: ({ children }) => (
    <Box
      sx={{
        borderLeft: '4px solid',
        borderColor: 'primary.main',
        backgroundColor: theme.palette.mode === 'dark' 
          ? alpha(theme.palette.primary.main, 0.08)
          : alpha(theme.palette.primary.main, 0.04),
        px: 3,
        py: 2,
        my: 3,
        borderRadius: '0 8px 8px 0',
        '& p:last-child': {
          mb: 0,
        },
      }}
    >
      {children}
    </Box>
  ),
  a: ({ href, children }) => (
    <Link
      href={href}
      target={href?.startsWith('http') ? '_blank' : undefined}
      rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
      sx={{
        color: 'primary.main',
        textDecoration: 'none',
        '&:hover': {
          textDecoration: 'underline',
        },
      }}
    >
      {children}
    </Link>
  ),
});

const PrivacyPage = () => {
  const theme = useTheme();

  const sections = [
    {
      id: 'introduction',
      title: 'Introduction',
      icon: <Lock />,
      content: `This Privacy Policy describes how Kief Studio LLC ("we," "our," or "us") collects, uses, and protects your information when you use PWGen-Rust, our password management software, and visit our website at https://pwgenrust.dev.

PWGen-Rust is designed with privacy as a core principle. We are committed to protecting your personal information and being transparent about our data practices.`
    },
    {
      id: 'data-collection',
      title: 'Information We Collect',
      icon: <Storage />,
      content: `**Local Data Storage:**
PWGen-Rust operates on a zero-knowledge architecture, meaning:
- Your passwords and sensitive data are stored locally on your device
- We do not have access to your vault contents
- Your master password is never transmitted to our servers
- All encryption and decryption happens on your device

**Website Analytics:**
Our website uses Kief Studio's own self-hosted analytics. When you visit, it records:
- Basic analytics data (page views, session duration, browser type)
- IP address, hashed with a key that changes daily
- Referral information
- Device and browser information

It sets no cookies, keeps only a per-tab session id, and does not run if your browser sends Do Not Track or Global Privacy Control.

**No Personal Data Collection:**
We do not collect, store, or process:
- Your passwords or vault contents
- Personal identification information
- Payment information (PWGen-Rust is free and open source)
- Contact information (unless voluntarily provided for support)`
    },
    {
      id: 'data-use',
      title: 'How We Use Information',
      icon: <Analytics />,
      content: `**Local Data:**
- Your vault data remains entirely on your device
- Used solely for the functionality of PWGen-Rust
- Never transmitted to our servers or third parties

**Website Analytics:**
- To improve our website and user experience
- To understand usage patterns and popular content
- To optimize website performance
- For security monitoring and abuse prevention

**Support Communications:**
If you contact us for support:
- We use your information only to respond to your inquiry
- Communications are not stored beyond the resolution of your issue
- No personal data is shared with third parties`
    },
    {
      id: 'data-sharing',
      title: 'Information Sharing',
      icon: <Shield />,
      content: `We do not sell, trade, or otherwise transfer your personal information to third parties. This includes:

**Zero Data Sharing:**
- Your vault contents are never shared (we don't have access to them)
- Website analytics are aggregated and anonymized
- No personal information is sold to advertisers or data brokers

**Legal Requirements:**
We may disclose information only if required by law, such as:
- Response to valid legal process (subpoenas, court orders)
- Protection of our rights or property
- Investigation of potential violations of our terms

**Service Providers:**
We may use trusted third-party services for:
- Website hosting and content delivery
- These providers are contractually bound to protect your data`
    },
    {
      id: 'security',
      title: 'Data Security',
      icon: <Security />,
      content: `**Technical Safeguards:**
- AES-256-GCM encryption for all vault data
- Zero-knowledge architecture - we cannot access your data
- Memory-safe Rust implementation prevents common vulnerabilities
- Secure key derivation using industry-standard algorithms

**Organizational Safeguards:**
- Limited access to any collected data
- Regular security audits and updates
- Secure development practices
- Incident response procedures

**Your Security:**
- Use a strong, unique master password
- Keep your software updated
- Enable device security features (screen lock, encryption)
- Regularly backup your vault using PWGen-Rust's export features`
    },
    {
      id: 'cookies',
      title: 'Cookies and Tracking',
      icon: <Cookie />,
      content: `**Essential Cookies:**
Our website uses minimal cookies for:
- Basic functionality and user preferences
- Security and fraud prevention
- These are necessary for the website to function properly

**Analytics:**
Our analytics are Kief Studio's own self-hosted analytics, used to understand traffic and protect the site. They:
- Set no cookies and keep only a per-tab session id
- Hash IP addresses with a key that changes daily
- Respect Do Not Track and Global Privacy Control

**No Tracking:**
We do not use:
- Third-party advertising cookies
- Social media tracking pixels
- Cross-site tracking technologies
- Behavioral advertising networks`
    },
    {
      id: 'rights',
      title: 'Your Privacy Rights',
      icon: <Lock />,
      content: `**Access and Control:**
- Your vault data is always under your control
- You can export or delete your data at any time
- No account registration required for basic functionality

**Communication Preferences:**
- Opt out of any non-essential communications
- Control your interaction with our support channels
- Delete any support communications upon request

**Data Portability:**
- Export your vault in standard formats
- No lock-in - migrate to other password managers
- Open source codebase for transparency

**Regional Rights:**
Depending on your location, you may have additional rights under GDPR, CCPA, or other privacy laws. Contact us to exercise these rights.`
    },
    {
      id: 'children',
      title: 'Children\'s Privacy',
      icon: <Shield />,
      content: `PWGen-Rust is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you believe we have inadvertently collected such information, please contact us immediately so we can delete it.`
    },
    {
      id: 'changes',
      title: 'Policy Changes',
      icon: <Analytics />,
      content: `We may update this Privacy Policy to reflect changes in our practices or for legal requirements. When we make changes:

- We will post the updated policy on our website
- We will update the "Last Updated" date
- For significant changes, we may provide additional notice
- Continued use of PWGen-Rust after changes constitutes acceptance

We encourage you to review this policy periodically to stay informed about how we protect your privacy.`
    }
  ];

  const contactInfo = [
    {
      label: 'Email',
      value: 'privacy@kief.studio',
      icon: <Email />
    },
    {
      label: 'Phone',
      value: '+1 617 418 4700',
      icon: <Phone />
    },
    {
      label: 'Website',
      value: 'https://kief.studio',
      icon: <Language />,
      isLink: true
    }
  ];

  return (
    <Box sx={{ py: { xs: 8, md: 12 }, minHeight: '100vh' }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ mb: 6, textAlign: 'center' }}>
          <Typography
            variant="h1"
            sx={{
              fontWeight: 800,
              mb: 3,
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Privacy Policy
          </Typography>
          <Typography
            variant="h5"
            color="text.secondary"
            sx={{ mb: 4, maxWidth: 800, mx: 'auto' }}
          >
            How we protect and handle your information
          </Typography>
          
          <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap" useFlexGap>
            <Chip
              icon={<Lock />}
              label="Zero-Knowledge Architecture"
              color="primary"
              variant="filled"
            />
            <Chip
              icon={<Security />}
              label="Local Data Storage"
              color="secondary"
              variant="outlined"
            />
          </Stack>
        </Box>

        {/* Last Updated */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 4,
            textAlign: 'center',
            backgroundColor: theme.palette.mode === 'dark'
              ? 'rgba(25, 118, 210, 0.1)'
              : 'rgba(25, 118, 210, 0.05)',
            border: '1px solid',
            borderColor: 'primary.main',
            borderRadius: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            <strong>Last Updated:</strong> January 2025
          </Typography>
        </Paper>

        {/* Main Content */}
        <Stack spacing={4}>
          {sections.map((section, index) => (
            <Paper
              key={section.id}
              elevation={0}
              sx={{
                p: 4,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: 'primary.main',
                  boxShadow: 2,
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box sx={{ color: 'primary.main', mr: 2 }}>
                  {section.icon}
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {section.title}
                </Typography>
              </Box>
              
              <ReactMarkdown
                components={MarkdownComponents(theme)}
                remarkPlugins={[remarkGfm]}
              >
                {section.content}
              </ReactMarkdown>
            </Paper>
          ))}
        </Stack>

        <Divider sx={{ my: 6 }} />

        {/* Contact Information */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            border: '1px solid',
            borderColor: 'primary.main',
            borderRadius: 2,
            background: theme.palette.mode === 'dark'
              ? 'rgba(25, 118, 210, 0.1)'
              : 'rgba(25, 118, 210, 0.05)',
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, textAlign: 'center' }}>
            Contact Us About Privacy
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 3, textAlign: 'center', color: 'text.secondary' }}>
            If you have questions about this Privacy Policy or our data practices, please contact us:
          </Typography>
          
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, flexWrap: 'wrap' }}>
            {contactInfo.map((item, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ color: 'primary.main' }}>
                  {item.icon}
                </Box>
                {item.isLink ? (
                  <Link
                    href={item.value}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: 'primary.main', textDecoration: 'none' }}
                  >
                    {item.value}
                  </Link>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {item.value}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default PrivacyPage;